import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DataTable from "../../components/DataGrids/DataGrid";
import FlexBetween from "../../components/FlexBetween";
import { getValidationRunScoringConfigBackup } from "../../services/scoringService";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const AuditValidationRunScoring: React.FC = () => {
  const { runId, resourceId } = useParams<{
    runId: string;
    resourceId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [pSize, setPSize] = useState(25);

  useEffect(() => {
    if (runId && resourceId) {
      setLoading(true);
      getValidationRunScoringConfigBackup({
        runId: Number(runId),
        resourceId: Number(resourceId),
        page,
        pSize,
      })
        .then((r) => setData(r || { items: [], total: 0 }))
        .finally(() => setLoading(false));
    }
  }, [runId, resourceId, page, pSize]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: t("auditValidationRunScoring.columns.id"),
      width: 70,
    },
    {
      field: "severity",
      headerName: t("auditValidationRunScoring.columns.severity"),
      width: 110,
    },
    {
      field: "factor",
      headerName: t("auditValidationRunScoring.columns.factor"),
      width: 90,
    },
    {
      field: "version",
      headerName: t("auditValidationRunScoring.columns.version"),
      width: 90,
    },
    {
      field: "is_active",
      headerName: t("auditValidationRunScoring.columns.active"),
      width: 80,
    },
    {
      field: "created_on",
      headerName: t("auditValidationRunScoring.columns.createdOn"),
      width: 180,
      valueFormatter: (params: any) => {
        const val = params?.value ?? params;
        return val ? new Date(val).toLocaleString() : "";
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          {t("auditValidationRunScoring.back")}
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("auditValidationRunScoring.header", { runId, resourceId })}
        </Typography>
      </Box>
      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={data?.items || []}
          loading={loading}
          dataListTitle={t("auditValidationRunScoring.header", {
            runId,
            resourceId,
          })}
          className="dataTable no-radius"
          paginationMode="server"
          rowCount={data?.total || 0}
          paginationModel={{ page: page - 1, pageSize: pSize }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
        />
      </FlexBetween>
    </Box>
  );
};

export default AuditValidationRunScoring;
